import React, { useEffect, useMemo, useRef, useState } from "react";
import { Radio, Music, ExternalLink, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { findMyTrack, hasCelebrated, markCelebrated, celebrate } from "@/lib/djCelebration";

/** Poll interval when the browser tab is visible. */
const POLL_MS = 6000;

const fmt = (ms) => {
  const s = Math.max(0, Math.floor((ms || 0) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/** Live "Now playing" widget — polls the couple's Spotify and animates progress locally. */
const NowPlaying = () => {
  const { t } = useLang();
  const [data, setData] = useState(null); // { playing, track?, reason? }
  const [localProgress, setLocalProgress] = useState(0);
  const [celebration, setCelebration] = useState(null); // { name, artist } — visible banner
  const lastFetchRef = useRef({ ts: 0, progress: 0 });
  const lastTrackIdRef = useRef(null);

  // Poll the backend for the current track.
  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const { data: res } = await api.get("/dj/now-playing");
        if (cancelled) return;
        setData(res);
        if (res?.track) {
          lastFetchRef.current = { ts: Date.now(), progress: res.track.progress_ms || 0 };
          setLocalProgress(res.track.progress_ms || 0);

          // Confetti trigger — detect a fresh track transition that matches something
          // this guest submitted from this browser.
          const currentId = res.track.id;
          if (currentId && currentId !== lastTrackIdRef.current) {
            lastTrackIdRef.current = currentId;
            const mine = findMyTrack(currentId);
            if (mine && !hasCelebrated(currentId) && res.playing) {
              markCelebrated(currentId);
              celebrate();
              setCelebration({ name: mine.name || res.track.name, artist: mine.artist || res.track.artist });
              setTimeout(() => setCelebration(null), 6500);
            }
          }
        } else if (!res?.playing) {
          lastTrackIdRef.current = null;
        }
      } catch {
        // silent — widget will just stay hidden
      }
    };

    tick();
    const iv = setInterval(tick, POLL_MS);
    const onVis = () => document.visibilityState === "visible" && tick();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Between polls, tick the local progress bar so it feels smooth (only when playing).
  useEffect(() => {
    if (!data?.playing || !data?.track) return;
    const iv = setInterval(() => {
      const elapsed = Date.now() - lastFetchRef.current.ts;
      const target = lastFetchRef.current.progress + elapsed;
      setLocalProgress(Math.min(target, data.track.duration_ms || 0));
    }, 500);
    return () => clearInterval(iv);
  }, [data]);

  const pct = useMemo(() => {
    if (!data?.track?.duration_ms) return 0;
    return Math.min(100, (localProgress / data.track.duration_ms) * 100);
  }, [localProgress, data]);

  // The widget itself hides when nothing is playing. But if a celebration is
  // active, we still render the celebratory banner overlay.
  if (!data?.playing || !data?.track) {
    if (!celebration) return null;
    return (
      <div
        className="surface-card p-5 flex items-center gap-4 bg-accent/10 border-accent/30"
        data-testid="dj-celebration-banner-standalone"
      >
        <Sparkles className="w-6 h-6 text-accent shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="editorial-kicker text-accent">{t("dj.yourSongPlaying") || "Your song is playing"}</p>
          <p className="mt-1 font-serif-display text-lg truncate">{celebration.name}</p>
          <p className="text-sm text-foreground/60 truncate">{celebration.artist}</p>
        </div>
      </div>
    );
  }

  const { track } = data;

  return (
    <a
      href={track.url || undefined}
      target={track.url ? "_blank" : undefined}
      rel="noopener noreferrer"
      className={
        "surface-card p-4 sm:p-5 flex items-center gap-4 group transition-colors " +
        (celebration
          ? "border-accent/60 bg-accent/[0.06] ring-1 ring-accent/40"
          : "hover:border-foreground/40")
      }
      data-testid="dj-nowplaying"
    >
      {track.cover ? (
        <img src={track.cover} alt="" className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-cover shrink-0 shadow-sm" />
      ) : (
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-foreground/5 flex items-center justify-center shrink-0">
          <Music className="w-6 h-6 text-foreground/30" />
        </div>
      )}

      <div className="min-w-0 flex-1">
        {celebration ? (
          <p className="editorial-kicker inline-flex items-center gap-1.5 text-accent" data-testid="dj-celebration-inline">
            <Sparkles className="w-3 h-3" />
            {t("dj.yourSongPlaying") || "Your song is playing"}
          </p>
        ) : (
          <p className="editorial-kicker inline-flex items-center gap-1.5 text-accent">
            <span className="relative inline-flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-accent animate-ping opacity-60" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-accent" />
            </span>
            <Radio className="w-3 h-3" />
            {t("dj.nowPlaying") || "Playing now"}
          </p>
        )}
        <p className="mt-1.5 font-serif-display text-lg sm:text-xl truncate">{track.name}</p>
        <p className="text-sm text-foreground/60 truncate">{track.artist}</p>

        {/* Progress bar */}
        <div className="mt-3 flex items-center gap-2 text-[10px] tabular-nums text-foreground/45">
          <span data-testid="dj-nowplaying-elapsed">{fmt(localProgress)}</span>
          <div className="flex-1 h-1 rounded-full bg-foreground/10 overflow-hidden">
            <div
              className="h-full bg-accent/80 transition-[width] duration-500 ease-linear"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span data-testid="dj-nowplaying-duration">{fmt(track.duration_ms)}</span>
        </div>
      </div>

      {track.url && (
        <ExternalLink className="hidden sm:block w-4 h-4 text-foreground/30 group-hover:text-foreground/70 transition-colors shrink-0" />
      )}
    </a>
  );
};

export default NowPlaying;
